import DOMPurify from 'dompurify';
import { Children, type ReactNode, useMemo } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import remarkGfm from 'remark-gfm';

function childrenToString(node: ReactNode): string {
  let out = '';
  Children.forEach(node, (child) => {
    if (child === null || child === undefined || typeof child === 'boolean') return;
    if (typeof child === 'string' || typeof child === 'number') {
      out += String(child);
    } else if (Array.isArray(child)) {
      out += childrenToString(child);
    } else if (typeof child === 'object' && 'props' in child) {
      const props = (child as { props?: { children?: ReactNode } }).props;
      out += childrenToString(props?.children ?? null);
    }
  });
  return out;
}

const NODE_LABELS = [
  'Class',
  'Function',
  'Method',
  'Interface',
  'File',
  'Folder',
  'Variable',
  'Enum',
  'Type',
  'CodeElement',
] as const;

const CITATION_RE = new RegExp(`\\[\\[(?:graph:)?(${NODE_LABELS.join('|')}):([^\\]]+)\\]\\]`, 'g');

export interface MarkdownRendererProps {
  readonly content: string;
  readonly onCitationClick?: (label: string, name: string) => void;
}

export function MarkdownRenderer({ content, onCitationClick }: MarkdownRendererProps): JSX.Element {
  const sanitized = useMemo(() => DOMPurify.sanitize(content), [content]);
  const segments = useMemo(() => splitOnCitations(sanitized), [sanitized]);
  const components = useMemo<Components>(
    () => ({
      code: ({ className, children, ...props }) => {
        const text = childrenToString(children).replace(/\n$/, '');
        const match = /language-(\w+)/.exec(className ?? '');
        if (!match) {
          return (
            <code className={className} {...props}>
              {children}
            </code>
          );
        }
        return (
          <SyntaxHighlighter
            style={oneDark}
            language={match[1]}
            PreTag="div"
            customStyle={{
              margin: 0,
              padding: '0.75rem',
              borderRadius: 8,
              fontSize: 12,
              background: '#0a0a10',
            }}
          >
            {text}
          </SyntaxHighlighter>
        );
      },
    }),
    [],
  );

  return (
    <div className="chat-prose">
      {segments.map((seg, i) =>
        seg.kind === 'citation' ? (
          <button
            key={i}
            type="button"
            onClick={() => onCitationClick?.(seg.label, seg.name)}
            className="code-ref-btn mx-0.5 inline-flex items-center gap-1 rounded-md border border-emerald-400/30 bg-emerald-500/10 px-1.5 py-0 align-baseline font-mono text-[11px] text-emerald-300 transition-colors hover:border-emerald-400/60 hover:bg-emerald-500/20"
            title={`${seg.label}:${seg.name}`}
          >
            <span className="font-mono text-[10px] uppercase tracking-wider opacity-70">
              {seg.label}
            </span>
            <span className="text-emerald-200">{seg.name}</span>
          </button>
        ) : (
          <ReactMarkdown key={i} remarkPlugins={[remarkGfm]} components={components}>
            {seg.text}
          </ReactMarkdown>
        ),
      )}
    </div>
  );
}

type Segment =
  | { readonly kind: 'text'; readonly text: string }
  | { readonly kind: 'citation'; readonly label: string; readonly name: string };

function splitOnCitations(input: string): Segment[] {
  const out: Segment[] = [];
  let cursor = 0;
  for (const match of input.matchAll(CITATION_RE)) {
    const idx = match.index ?? 0;
    if (idx > cursor) out.push({ kind: 'text', text: input.slice(cursor, idx) });
    out.push({ kind: 'citation', label: match[1] ?? '', name: (match[2] ?? '').trim() });
    cursor = idx + match[0].length;
  }
  if (cursor < input.length) out.push({ kind: 'text', text: input.slice(cursor) });
  return out;
}
